import { create } from "zustand";

interface BackendStatusState {
  /** True once an API call failed because the backend was unreachable. */
  unreachable: boolean;
  markUnreachable: () => void;
  markReachable: () => void;
}

/**
 * Whether the backend is currently reachable. Written from `api.ts` — the one
 * place every backend call passes through, so screens that fetch from a bare
 * `useEffect` count too — and read by `App.tsx`, which swaps the whole route
 * tree for the offline screen while it's set.
 *
 * This tracks *our server*, not the device's connection: a dead backend and a
 * dead Wi-Fi are indistinguishable from the client, and get the same screen.
 */
export const useBackendStatusStore = create<BackendStatusState>(set => ({
  unreachable: false,
  // Both actions return the existing state when nothing changes, so zustand
  // skips notifying subscribers — successful requests are a hot path, and when
  // the backend does go down several queries usually fail at once.
  markUnreachable: () =>
    set(state => (state.unreachable ? state : { unreachable: true })),
  markReachable: () =>
    set(state => (state.unreachable ? { unreachable: false } : state)),
}));
